const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';

// Utility to derive a friendly class from the color value & label
const colorToClass = (value, label) => {
  if (!value) return '';
  const varMatch = /^var\(--([^)]+)\)/.exec(value);
  if (varMatch) return `color-var-${varMatch[1].toLowerCase()}`;
  if (value.startsWith('#')) return `color-${value.slice(1).toLowerCase()}`;
  if (label) return `color-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return '';
};

const EmblaCarousel = ({ data, id }) => {
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.search?.subrequests?.[id]);

  const getImageUrl = (item) => {
    if (item.image_scales && item.image_field) {
      return `${item['@id']}/@@images/${item.image_field}`;
    }
    if (item.preview_image) return `${item.preview_image['@id']}/@@images/image`;
    if (item.lead_image) return `${item.lead_image['@id']}/@@images/image`;
    if (item.image) {
      const image = Array.isArray(item.image) ? item.image[0] : item.image;
      return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
    }
    return '';
  };

  const getListingImageUrl = (item) => {
    if (item['@type'] === 'Image') return `${item['@id']}/@@images/image`;
    return getImageUrl(item);
  };

  const formatEffectiveDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const convertItemToSlide = (item) => ({
    heading: item.title || '',
    content: item.description || '',
    image: null,
    link: item,
    buttonText: data.listingButtonText || 'Read more',
    effectiveDate: item.effective || null,
    isFromListing: true,
  });

  const getSlides = () => {
    let slides = [];
    if (data.useListing && searchResults?.items && searchResults.items.length > 0) {
      slides = searchResults.items.map(convertItemToSlide);
    }
    if (!data.useListing || (data.useListing && data.appendManualSlides)) {
      const manualSlides = (data.slides || []).map(slide => ({
        ...slide,
        isFromListing: false
      }));
      slides = slides.concat(manualSlides);
    }
    return slides;
  };

  const slides = getSlides();
  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data.minSlidesOnMobile, 10) || 1;
  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(() => {
    const isMobileInit = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    return isMobileInit ? Math.max(1, minSlidesOnMobile) : slidesToShow;
  });

  useEffect(() => {
    const calculateSlidesToShow = () => {
      const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
      setEffectiveSlidesToShow(isMobile ? Math.max(1, minSlidesOnMobile) : slidesToShow);
    };
    calculateSlidesToShow();
    window.addEventListener('resize', calculateSlidesToShow);
    return () => window.removeEventListener('resize', calculateSlidesToShow);
  }, [slidesToShow, minSlidesOnMobile]);

  useEffect(() => {
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: [
          'title', 
          'description', 
          'image_field',
          'image_scales',
          'effective',
          'remoteUrl'
        ],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          const transformedQuery = {};
          data.query.query.forEach(criterion => {
            if (criterion.i === 'portal_type') {
              transformedQuery.portal_type = criterion.v;
            } else if (criterion.i === 'path') {
              transformedQuery.path = criterion.v;
            } else if (criterion.i === 'Subject') {
              transformedQuery.Subject = criterion.v;
            } else if (criterion.i === 'review_state') {
              transformedQuery.review_state = criterion.v;
            }
          });
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order) transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit) transformedQuery.b_size = parseInt(data.query.limit, 10);
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit) searchOptions.b_size = parseInt(data.query.limit, 10);
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = baseOptions;
      } else {
        searchOptions = { ...baseOptions, query: data.query };
      }

      dispatch(searchContent(searchTerm, searchOptions, id));
    }
  }, [data.useListing, data.query, dispatch, id]);

  const carouselType = data.carouselType || 'default';

  const configOverrides = {
    default: { align: 'start', loop: data.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };

  const presetConfig = getPresetConfig(data.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };

  const [selectedIndex, setSelectedIndex] = useState(0);

  const plugins = data.autoplay ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 4000, stopOnInteraction: false })] : [];
  const [viewportRef, embla] = useEmblaCarousel({
    ...config,
    loop: data.loop ?? true,
    align: 'start',
    slidesToScroll: effectiveSlidesToShow,
  }, plugins);

  

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    onSelect(); // set initial selected index
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      try { embla.off('select', onSelect); } catch (e) {}
      try { embla.off('reInit', onSelect); } catch (e) {}
    };
  }, [embla]);
const getHref = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return '#';
    if (item['@type'] === 'Link' && item.remoteUrl) return item.remoteUrl;
    const url = item['@id'] || '';
    const isFile = item['@type'] === 'File';
    return url ? (isFile ? `${url}/@@download/file` : url) : '#';
  };

  const _displayMode = data.displayMode || data.mode || 'full';
  const isLogoMarquee = _displayMode === 'logo-marquee';
  const variantClass = `type-${_displayMode}`;
  const isImageOnly = _displayMode === 'image-only';
  const isImageTopMode = _displayMode === 'image-top';

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons || _displayMode === 'image-only') return null;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    return (
      <a
        href={href}
        className="carousel-button"
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;
    const href = getHref(slide.link);
    if (!href || href === '#') return;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  const imageFor = (slide) => {
    return slide.isFromListing
      ? getListingImageUrl(slide.link) 
      : (() => {
          const image = Array.isArray(slide.image) ? slide.image[0] : slide.image;
          return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
        })();
  };

  const heightClass = data.equalHeight ? 'equal-height' : '';
  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const colorClass = colorToClass(data.backgroundColor);
  const toneClass = data.textTone === 'light' ? 'tone-light' : 'tone-dark';

  const showArrows = data.hideArrows !== true && slides.length > 1 && !isLogoMarquee;
  const showDots = data.hideDots !== true && slides.length > 0 && !isLogoMarquee;

  const pageCount = Math.ceil(slides.length / effectiveSlidesToShow);

  return (
    <div
      className={`embla ${heightClass} ${data.isFullWidth ? 'full-width' : ''} align-${data.alignment || 'left'} ${modeClass} ${variantClass} ${toneClass} ${colorClass} ${data.outerClassName || ''}`}
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      <div className="embla__inner">

        {/* Logo marquee */}
        {isLogoMarquee ? (
          <>
            {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
            <div
              className={`logo-marquee ${data.pauseOnHover ? 'pause-on-hover' : ''}`}
              style={{
                '--marquee-duration': `${parseInt(data.marqueeSpeed, 10) || 20}s`,
                '--logo-height': `${parseInt(data.logoHeight, 10) || 48}px`,
                '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
                '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
              }}
            >
              <div className="logo-marquee__track">
                {[...slides, ...slides].map((slide, i) => {
                  const src = imageFor(slide);
                  if (!src) return null;
                  const alt = slide.heading || 'Logo';
                  const href = getHref(slide.link);
                  const clickable = data.clickableSlides && href && href !== '#';
                  const img = <img className="logo-marquee__img" src={src} alt={alt} />;
                  return (
                    <div className="logo-marquee__item" key={`r-${i}`}>
                      {clickable ? (
                        <a
                          href={href}
                          className="logo-marquee__link"
                          target="_blank"
                        >
                          {img}
                        </a>
                      ) : img}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="embla__viewport" ref={viewportRef}>
              <div className="embla__container">
                {slides.map((slide, index) => {
                  const imageUrl = imageFor(slide);
                  const href = getHref(slide.link);
                  const isClickable = data.clickableSlides && href && href !== '#';

                  return (
                    <div className="embla__slide" key={index} style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}>
                      <div className="slide__content">
                      <div
                        className={`carousel-slide-inner ${isClickable ? 'clickable' : ''} ${isImageOnly ? 'image-only' : ''} ${isImageTopMode ? 'image-top' : ''} ${slide.isFromListing ? 'listing-slide' : 'manual-slide'}`}
                        style={{ 
                          backgroundImage: !isImageOnly && !isImageTopMode && imageUrl ? `url(${imageUrl})` : 'none',
                          cursor: isClickable ? 'pointer' : 'default'
                        }}
                        onClick={() => handleSlideClick(slide)}
                      >
                        {isImageOnly ? (
                          imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={slide.heading || 'Slide image'} 
                            />
                          ) : (
                            <div className="image-placeholder">
                              No image
                            </div>
                          )
                        ) : isImageTopMode ? (
                          <div className="card card-image-top">
                            {imageUrl ? (
                              <div className="card-media">
                                <img src={imageUrl} alt={slide.heading || 'Slide image'} />
                              </div>
                            ) : null}
                            <div className="card-content">
                              <h3>{slide.heading}</h3>
                              {!data.hideDescription && <p>{slide.content}</p>}
                              {data.showEffectiveDate && slide.effectiveDate && (
                                <p className="effective-date">{formatEffectiveDate(slide.effectiveDate)}</p>
                              )}
                              {renderLink(slide)}
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3>{slide.heading}</h3>
                            {!data.hideDescription && <p>{slide.content}</p>}
                            {data.showEffectiveDate && slide.effectiveDate && (
                              <p className="effective-date">{formatEffectiveDate(slide.effectiveDate)}</p>
                            )}
                            {renderLink(slide)}
                          </>
                        )}
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {data.title ? <h2 className="embla__title">{data.title}</h2> : null}

            {showArrows && slides.length > 1 && (
              <div className="embla__controls" aria-hidden="false">
                <div className="embla__controls__buttons" aria-hidden="false">
                  <button className="embla__prev" onClick={() => embla && embla.scrollPrev()}><i className="bi bi-arrow-left-short"></i></button>
                  <button className="embla__next" onClick={() => embla && embla.scrollNext()}><i className="bi bi-arrow-right-short"></i></button>
                </div>
              </div>
            )}

            {showDots && pageCount > 1 && (
              <div className="embla__nav">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => embla && embla.scrollTo(i)}
                    className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmblaCarousel;